"use client";
import React, { useState } from "react";
import styles from "../css/index.js";
import Button from "../../../../components/Button";
import TracerLabel from './TracerLabel';

export default function LabelPreview({
  form,
  forms,
  pharmacyInfo,
  activeTab,
  onClose,
  showClose = false,
  showWrapper = false,
  receiptNumber,
  drugInfo,
  user, // Add user prop to access logged-in user data
  onDrugInfoUpdate, // Add callback to pass drug info back to parent
  showRepeat = false, // Add prop to control repeat text display
  repeatData = null, // Add prop for actual repeat data
  repeatNumber = null,
  ...props
}) {
  const [drugInfoState, setDrugInfo] = useState({
    strength: null,
    dosageForm: null,
    schedule: null,
    packSize: ""
  });

  const effectiveShowRepeat = Boolean(
    showRepeat ||
      (repeatData &&
        (Number(repeatData.repeatsTotal) > 0 ||
          String(repeatData.displayText || "").trim().length > 0))
  );

  // Use drug info from props if provided, otherwise use empty state
  const finalDrugInfo = drugInfo || {
    strength: null,
    dosageForm: null,
    schedule: null,
    packSize: ""
  };

  // Debug practitioner data
  if (form && form.data) {
    console.log('=== LABEL PREVIEW PRACTITIONER DEBUG ===');
    console.log('Form data practitioner:', form.data.practitioner);
    console.log('Practitioner surname:', form.data.practitioner?.surname);
    console.log('Practitioner firstname:', form.data.practitioner?.firstname);
    console.log('Practitioner object keys:', form.data.practitioner ? Object.keys(form.data.practitioner) : 'No practitioner object');
  }

  // Debug repeat data
  console.log('=== LABEL PREVIEW REPEAT DEBUG ===');
  console.log('showRepeat prop:', showRepeat);
  console.log('repeatData prop:', repeatData);
  console.log('showRepeat value:', showRepeat);
  console.log('repeatData?.displayText:', repeatData?.displayText);

  // Function to extract patient title from patient name
  const extractPatientTitle = (patientName) => {
    if (!patientName) return null;
    
    const titlePatterns = [
      /\bMR\b/i,
      /\bMISS\b/i,
      /\bMRS\b/i,
      /\bMS\b/i,
      /\bDR\b/i,
      /\bPROF\b/i
    ];
    
    for (const pattern of titlePatterns) {
      const match = patientName.match(pattern);
      if (match) {
        return match[0].toUpperCase();
      }
    }
    
    return null;
  };

  // Function to extract dependent code and main member ID from patient name
  const extractPatientCodes = (patientName) => {
    if (!patientName) return { dependentCode: '', mainMemberId: '' };
    
    // Look for pattern like "dependentcode/mainmemberid" at the end
    const codeMatch = patientName.match(/(\d+)\/(\d+)$/);
    if (codeMatch) {
      return {
        dependentCode: codeMatch[1],
        mainMemberId: codeMatch[2]
      };
    }
    
    return { dependentCode: '', mainMemberId: '' };
  };

  // Function to clean patient name by removing title and codes
  const cleanPatientName = (patientName) => {
    if (!patientName) return '';
    
    let cleanedName = patientName;
    
    // Remove title
    const title = extractPatientTitle(patientName);
    if (title) {
      // Remove the title from the beginning
      const titlePatterns = [
        new RegExp(`^${title}\\s+`, 'i'),
        /\bMR\s+/i,
        /\bMISS\s+/i,
        /\bMRS\s+/i,
        /\bMS\s+/i,
        /\bDR\s+/i,
        /\bPROF\s+/i
      ];
      
      for (const pattern of titlePatterns) {
        cleanedName = cleanedName.replace(pattern, '');
      }
    }
    
    // Remove codes at the end (pattern like " 01/123456" or "/01/123456")
    cleanedName = cleanedName.replace(/\s*\d+\/\d+$/, '').replace(/\/\d+\/\d+$/, '');
    
    return cleanedName.trim();
  };

  // Build the combined medication line
  const buildMedicationLine = () => {
    // Add null check for form and form.data
    if (!form || !form.data) {
      return <div>No medication data available</div>;
    }
    
    console.log('Building medication line for:', form.data.medication);
    console.log('Drug info:', drugInfo);
    console.log('Pack size:', drugInfo?.packSize);
    console.log('Schedule:', drugInfo?.schedule);

    // Extract strength from medication name if no drug info available
    let extractedStrength = "";
    if (!drugInfo?.strength && form.data.medication) {
      const strengthMatch = form.data.medication.match(/\b\d+(?:\.\d+)?\s?(?:mcg|µg|mg|g|ml|mL|IU)\b/i);
      if (strengthMatch) {
        extractedStrength = strengthMatch[0];
        console.log('Extracted strength from name:', extractedStrength);
      }
    }

    // Extract dosage form from medication name if no drug info available
    let extractedDosageForm = "";
    if (!drugInfo?.dosageForm && form.data.medication) {
      const forms = [
        /\btab(?:let)?s?\b/i,
        /\bcap(?:sule)?s?\b/i,
        /\bsuspension\b/i,
        /\bsyrup\b/i,
        /\bcream\b/i,
        /\bointment\b/i,
        /\binjection\b/i,
        /\bdrops\b/i,
        /\bsolution\b/i,
        /\belixir\b/i,
        /\bgel\b/i,
        /\blotion\b/i,
        /\bspray\b/i
      ];
      const formMatch = forms.find(f => f.test(form.data.medication));
      if (formMatch) {
        extractedDosageForm = formMatch.exec(form.data.medication)?.[0] || "";
        console.log('Extracted dosage form from name:', extractedDosageForm);
      }
    }

    // Build the three positioned sections
    const strengthToShow = drugInfo?.strength || extractedStrength;
    const dosageFormToShow = drugInfo?.dosageForm || extractedDosageForm;
    const packSizeToShow = drugInfo?.packSize || "";
    const itemCodeValue =
      repeatNumber != null && String(repeatNumber).trim() !== ''
        ? String(repeatNumber).trim()
        : '';

    return (
      <div className={styles.itemMedicationLine}>
        {/* LEFT: Name + Strength (fixed position) */}
        <span className={styles.medicationName}>
          {form.data.medication || ""}
          {strengthToShow && ` ${strengthToShow}`}
        </span>
        
        {/* CENTER: Dosage Form + Pack Size (fixed position) */}
        <span className={styles.medicationCenter}>
          {dosageFormToShow && `  ${dosageFormToShow}`}
          {dosageFormToShow && packSizeToShow && ` ${packSizeToShow}`}
          {!dosageFormToShow && packSizeToShow && packSizeToShow}
        </span>
        
        {/* RIGHT: Schedule (fixed position) */}
        <span className={styles.medicationEnd}>
          {itemCodeValue ? `(#${itemCodeValue}) ` : ''}
          {drugInfo && drugInfo.schedule !== undefined && drugInfo.schedule !== null && ` S${drugInfo.schedule}`}
        </span>
      </div>
    );
  };

  const Header = (
    <div className={styles.sectionHeader}>
      <span className={styles.sectionIcon}>👁️</span>
      <h2 className={styles.sectionTitle}>Label Preview</h2>
      <Button className={styles.tracerButton} variant="primary">{activeTab}</Button>
      {/* Print button removed - no longer functional */}
      {showClose && (
        <Button className={styles.closeButton} onClick={onClose} variant="primary">
          ✕
        </Button>
      )}
    </div>
  );

  const Card = activeTab === "Tracer Label" ? (
    <TracerLabel
      form={form}
      forms={forms}
      pharmacyInfo={pharmacyInfo}
      totals={{}}
      drugInfo={finalDrugInfo}
      user={user}
      repeatNumber={repeatNumber}
      showRepeat={effectiveShowRepeat}
      repeatData={repeatData}
    />
  ) : (
    <div key={form?.id || 'item-label'} className={styles.labelCard}>
      <div className={styles.pharmacyHeader}>
        <h3 className={styles.pharmacyName}>{pharmacyInfo.name}</h3>
        <p className={styles.pharmacyDetails}>{pharmacyInfo.address}</p>
        <p className={styles.pharmacyPhone}>{pharmacyInfo.phone}</p>
      </div>

      <div className={styles.prescriptionInfo}>
        {/* Grey divider line between phone and medication */}
        <div className={styles.dividerLine}></div>
        
        {buildMedicationLine()}

        <div className={styles.directionsLine}>
          <span>{form?.data?.directions || ''}</span>
        </div>

        {/* Repeat line with RX centered - only show if repeat data exists */}
        {effectiveShowRepeat && (
          <div className={styles.repeatLine}>
            <span>Repeat {repeatData?.displayText || '1 OF 5'}</span>
            <span className={styles.centeredRxNumber}>
              Rx : {form?.data?.rxNumber || ''}
            </span>
          </div>
        )}
        
        {/* RX line - show without repeat text if repeat data does not exist */}
        {!effectiveShowRepeat && (
          <div className={styles.repeatLine}>
            <span className={styles.centeredRxNumber}>
              Rx : {form?.data?.rxNumber || ''}
            </span>
          </div>
        )}
        
        <div className={styles.patientLine}>
          <span className={styles.patientName}>
            {(() => {
              const patientName = form?.data?.patientName || '';
              const title = extractPatientTitle(patientName);
              const codes = extractPatientCodes(patientName);
              const cleanedName = cleanPatientName(patientName);
              
              // Split cleaned name into parts to reorganize as SURNAME FIRSTNAME
              const nameParts = cleanedName.trim().split(/\s+/);
              let firstname = '';
              let surname = '';
              
              if (nameParts.length === 1) {
                firstname = nameParts[0];
                surname = '';
              } else if (nameParts.length === 2) {
                firstname = nameParts[0];
                surname = nameParts[1];
              } else {
                firstname = nameParts[0];
                surname = nameParts.slice(1).join(' ');
              }
              
              // Build display name: SURNAME FIRSTNAME TITLE
              let displayName = '';
              if (surname) displayName += surname.toUpperCase();
              if (firstname) displayName += (displayName ? ' ' : '') + firstname.toUpperCase();
              if (title) displayName += (displayName ? ' ' : '') + title.toUpperCase();
              
              return displayName || 'PATIENT';
            })()}
          </span>
          <span className={styles.patientCodes}>
            {(() => {
              const patientName = form?.data?.patientName || '';
              const codes = extractPatientCodes(patientName);
              return codes.dependentCode || codes.mainMemberId ? `${codes.dependentCode}/${codes.mainMemberId}` : '';
            })()}
          </span>
        </div>

        <div className={styles.dateLine}>
          <span>DOCTOR : {form?.data?.practitioner?.surname?.toUpperCase()} {form?.data?.practitioner?.firstname?.charAt(0)?.toUpperCase()}.</span>
          <span>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
        </div>

        <div className={styles.dispenserLine}>
          <span>DISPENSER : {user?.surname?.toUpperCase()} {user?.name?.toUpperCase()}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.previewSection}>
      {Header}
      <div className={styles.labelPreview}>{Card}</div>
    </div>
  );
}
